import React, { useState, useEffect } from 'react';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ArrowPathIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

interface ExecutionLog {
  id: string;
  flowId: string;
  flowName: string;
  status: 'running' | 'completed' | 'failed';
  startTime: Date;
  endTime?: Date;
  duration?: number;
  trigger: string;
  error?: string;
}

interface ExecutionLogsProps {
  organizationId: string;
}

const ExecutionLogs: React.FC<ExecutionLogsProps> = ({ organizationId }) => {
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'completed' | 'failed' | 'running'>('all');

  useEffect(() => {
    fetchLogs();
  }, [organizationId]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      // TODO: Implement actual API call
      // const response = await fetch(`${FLOW_ENGINE_ENDPOINTS.executions}?organizationId=${organizationId}`);
      // const data = await response.json();
      
      // Mock data for now
      const mockLogs: ExecutionLog[] = [
        {
          id: 'exec_1',
          flowId: 'flow_1',
          flowName: 'Email küldés',
          status: 'completed',
          startTime: new Date(Date.now() - 60000),
          endTime: new Date(Date.now() - 58000),
          duration: 2000,
          trigger: 'schedule'
        },
        {
          id: 'exec_2',
          flowId: 'flow_1',
          flowName: 'Email küldés',
          status: 'completed',
          startTime: new Date(Date.now() - 120000),
          endTime: new Date(Date.now() - 118000),
          duration: 2000,
          trigger: 'schedule'
        },
        {
          id: 'exec_3',
          flowId: 'flow_2',
          flowName: 'Napi riport',
          status: 'failed',
          startTime: new Date(Date.now() - 180000),
          endTime: new Date(Date.now() - 178000),
          duration: 2000,
          trigger: 'manual',
          error: 'SMTP connection failed'
        }
      ];
      
      setLogs(mockLogs);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    return log.status === filter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'running':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit'
    }).format(date);
  };

  return (
    <div className="space-y-4">
      {/* Filter Buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Összes ({logs.length})
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            filter === 'completed'
              ? 'bg-green-500 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Sikeres ({logs.filter(l => l.status === 'completed').length})
        </button>
        <button
          onClick={() => setFilter('failed')}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            filter === 'failed'
              ? 'bg-red-500 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Sikertelen ({logs.filter(l => l.status === 'failed').length})
        </button>
        <button
          onClick={() => setFilter('running')}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            filter === 'running'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Futó ({logs.filter(l => l.status === 'running').length})
        </button>
        <button
          onClick={fetchLogs}
          className="ml-auto px-3 py-1 rounded-md text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-1"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Frissítés
        </button>
      </div>

      {/* Logs List */}
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p>Logok betöltése...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>Nincs megjeleníthető log</p>
          </div>
        ) : (
          filteredLogs.map((log) => (
            <div
              key={log.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Status Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getStatusIcon(log.status)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                      {log.flowName}
                    </h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                      {log.status === 'completed' ? 'Sikeres' : log.status === 'failed' ? 'Sikertelen' : 'Futó'}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 mb-2">
                    <span>🕐 {formatTime(log.startTime)}</span>
                    <span>⏱️ {formatDuration(log.duration)}</span>
                    <span>🎯 {log.trigger === 'schedule' ? 'Ütemezett' : 'Manuális'}</span>
                  </div>

                  {log.error && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
                      <strong>Hiba:</strong> {log.error}
                    </div>
                  )}
                </div>

                {/* Execution ID */}
                <div className="flex-shrink-0 text-right">
                  <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    {log.id}
                  </code>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExecutionLogs;
